import { type FC, type ReactElement, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Body } from 'components/Text'
import Tooltip from 'components/Tooltip/Tooltip'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import { selectMarketInfo } from 'state/futures/common/selectors'
import { useAppSelector } from 'state/hooks'
import media from 'styles/media'

import { isMarketDataKey, marketDataKeyMap } from './utils'

type MarketDetailProps = {
	mobile?: boolean
	dataKey: string
	color?: string
	value: string | ReactElement
	extra?: ReactElement
	tooltipContent?: string
}

const MarketDetail: FC<MarketDetailProps> = memo(
	({ mobile, dataKey, color, value, extra, tooltipContent }) => {
		const { t } = useTranslation()
		const marketInfo = useAppSelector(selectMarketInfo)
		const pausedClass = marketInfo?.isSuspended ? 'paused' : ''

		const contentSuffix = useMemo(() => {
			if (dataKey === marketInfo?.marketName) {
				return 'market-key'
			} else if (isMarketDataKey(dataKey)) {
				return marketDataKeyMap[dataKey]
			} else {
				return ''
			}
		}, [dataKey, marketInfo])

		return (
			<MarketDetailsTooltip
				id={dataKey}
				trigger={
					<Container>
						<WithCursor cursor="help" rowGap="2px">
							<Body size={mobile ? 'small' : 'medium'} className="heading">
								{dataKey}
							</Body>
							<FlexDivRowCentered>
								<MarketDetailValue
									value={value}
									color={color}
									mobile={mobile}
									pausedClass={pausedClass}
								/>
							</FlexDivRowCentered>
						</WithCursor>
						{extra}
					</Container>
				}
				content={tooltipContent ?? t(`swaps.market-details-card.tooltips.${contentSuffix}`)}
				place="bottom"
				portal
			/>
		)
	}
)

export default MarketDetail

// Extend type of cursor to accept different style of cursor. Currently accept only 'help'
const WithCursor = styled(FlexDivCol)<{ cursor: 'help' }>`
	cursor: ${(props) => props.cursor};
`

const MarketDetailsTooltip = styled(Tooltip)`
	z-index: 2;
	padding: 10px;
	max-width: 300px;
	${media.lessThan('lg')`
		padding: 0 15px;
		border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
		margin: auto;
	`}
`

export const MarketDetailValue = ({
	value,
	color,
	pausedClass = '',
	mobile = false,
}: {
	value: string | ReactElement
	color?: string
	pausedClass?: 'paused' | ''
	mobile?: boolean
}) => (
	<Body
		as="span"
		mono
		size={mobile ? 'small' : 'medium'}
		className={`value ${color || ''} ${pausedClass}`}
	>
		{value}
	</Body>
)

const Container = styled(FlexDivRowCentered)`
	position: relative;
	overflow: visible;
`
